import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: Theme;
  /** Whether the currently-applied appearance is dark (resolves "system" to the OS preference). */
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  /** Flip between explicit light and dark. */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveIsDark(theme: Theme): boolean {
  if (typeof window === "undefined") return false;
  if (theme === "system") return window.matchMedia("(prefers-color-scheme: dark)").matches;
  return theme === "dark";
}

/**
 * Lightweight theme provider — toggles the `dark` class on <html> so shadcn's
 * Tailwind CSS variables flip. Defaults to `light` because the app is used in
 * clinical environments where light mode is the operating norm.
 */
export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "footagemgr-theme",
}: {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return defaultTheme;
    return (localStorage.getItem(storageKey) as Theme | null) ?? defaultTheme;
  });

  const [isDark, setIsDark] = useState<boolean>(() => resolveIsDark(theme));

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    const nextIsDark = resolveIsDark(theme);
    root.classList.add(nextIsDark ? "dark" : "light");
    setIsDark(nextIsDark);

    // If the user picked "system", keep in sync with OS changes while mounted.
    if (theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const sysDark = mq.matches;
      root.classList.remove("light", "dark");
      root.classList.add(sysDark ? "dark" : "light");
      setIsDark(sysDark);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = useCallback(
    (next: Theme) => {
      localStorage.setItem(storageKey, next);
      setThemeState(next);
    },
    [storageKey],
  );

  const toggle = useCallback(() => {
    setTheme(isDark ? "light" : "dark");
  }, [isDark, setTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, isDark, setTheme, toggle }),
    [theme, isDark, setTheme, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
